import { ClientRect } from './components/modalDropdownTypes';
import {
  isDropdownAiOption,
  isDropdownOption,
} from './components/settingsInterfaces';
import {
  DropdownExpandableSection,
  DropdownItem,
  DropdownOptionType,
} from './components/types';

const MIN_MARGIN_WINDOW = 20;
const MARGIN_MODAL_BOX = 2;

const getPositionAndMaxHeightFallback = (
  refBox: ClientRect,
  modalBox: ClientRect,
  windowHeight: number,
  windowWidth: number
) => {
  const maxHeight = modalBox.height;
  const left =
    refBox.left > windowWidth - refBox.right
      ? refBox.left - modalBox.width - MARGIN_MODAL_BOX
      : refBox.right + MARGIN_MODAL_BOX;
  const top =
    modalBox.height > windowHeight
      ? MARGIN_MODAL_BOX
      : (windowHeight - modalBox.height) / 2;
  return { left, top, maxHeight };
};

export type GetPositionAndMaxHeightVerticallyArgs = {
  refBox: ClientRect;
  modalBox: ClientRect;
  isScrollable?: boolean;
  forceAlignToRight?: boolean;
  verticalOffset?: number;
  includeBottom?: boolean;
};

export const getPositionAndMaxHeightVertically = ({
  refBox,
  modalBox,
  isScrollable = true,
  forceAlignToRight = false,
  verticalOffset = 0,
  includeBottom = false,
}: GetPositionAndMaxHeightVerticallyArgs) => {
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  let top = 0;
  let maxHeight = 0;
  let isAbove = false;
  if (
    windowHeight - refBox.bottom < refBox.top &&
    modalBox.height > windowHeight - refBox.bottom - MIN_MARGIN_WINDOW
  ) {
    // Render menu ABOVE the target
    isAbove = true;
    top =
      Math.max(
        MIN_MARGIN_WINDOW,
        refBox.top - modalBox.height - MARGIN_MODAL_BOX
      ) - verticalOffset;
    maxHeight = refBox.top - MIN_MARGIN_WINDOW - MARGIN_MODAL_BOX;
  } else {
    // Render menu BELOW the target
    top = refBox.bottom + MARGIN_MODAL_BOX + verticalOffset;
    maxHeight =
      windowHeight - refBox.bottom - MIN_MARGIN_WINDOW - MARGIN_MODAL_BOX;
  }

  const left =
    refBox.left + modalBox.width > windowWidth || forceAlignToRight
      ? refBox.right - modalBox.width
      : refBox.left;

  const result = clampToWindowBounds(
    isScrollable || modalBox.height <= maxHeight
      ? { left, top, maxHeight }
      : getPositionAndMaxHeightFallback(
          refBox,
          modalBox,
          windowHeight,
          windowWidth
        )
  );
  if (includeBottom && isAbove) {
    return {
      ...result,
      bottom: windowHeight - (result.top + maxHeight),
      refBoxWidth: refBox.width,
    };
  }
  return { ...result, refBoxWidth: refBox.width };
};

const clampToWindowBounds = ({
  left,
  top,
  maxHeight,
}: {
  left: number;
  top: number;
  maxHeight: number;
}) => {
  if (left < 0) {
    return {
      left: MIN_MARGIN_WINDOW,
      top,
      maxHeight,
      maxWidth: window.innerWidth - MIN_MARGIN_WINDOW * 2,
    };
  }

  return {
    left,
    top,
    maxHeight,
  };
};

export const getPositionAndMaxHeightHorizontally = (
  refBox: ClientRect,
  modalBox: ClientRect,
  leftOffset = 0
) => {
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  let top = 0;
  let maxHeight = 0;
  if (
    windowHeight - refBox.top < refBox.bottom &&
    modalBox.height > windowHeight - refBox.top - MIN_MARGIN_WINDOW
  ) {
    top = Math.max(MIN_MARGIN_WINDOW, refBox.bottom - modalBox.height);
    maxHeight = refBox.bottom - MIN_MARGIN_WINDOW;
  } else {
    top = refBox.top;
    maxHeight = windowHeight - refBox.top - MIN_MARGIN_WINDOW;
  }
  const left =
    refBox.right + modalBox.width + MIN_MARGIN_WINDOW > windowWidth
      ? refBox.left - modalBox.width - MARGIN_MODAL_BOX
      : refBox.right + MARGIN_MODAL_BOX;
  return { left: left + leftOffset, top, maxHeight, refBoxWidth: refBox.width };
};

const navigationable = new Set([
  DropdownOptionType.OPTION,
  DropdownOptionType.AI_OPTION,
  DropdownOptionType.SUBMENU,
  DropdownOptionType.EXPANDABLE_SECTION,
  DropdownOptionType.LINK,
  DropdownOptionType.BUTTON_OPTION,
]);

const isNavigationable = (option: DropdownItem) => {
  if (
    (isDropdownOption(option) &&
      (option.isDisabled ||
        (option as unknown as DropdownExpandableSection).forceExpand)) ||
    (isDropdownAiOption(option) && option.isDisabled)
  ) {
    return false;
  }
  return navigationable.has(option.type);
};

export const moveCursor = (
  options: DropdownItem[],
  start: number,
  delta: number
) => {
  let cursor = (start + options.length) % options.length;
  if (!options.some(isNavigationable)) return -1;
  while (true) {
    cursor = (cursor + options.length + delta) % options.length;
    if (isNavigationable(options[cursor])) {
      return cursor;
    }
  }
};

const inputTypes = new Set(['text', 'range']);

// TODO: handle more form input types
export const isFormElement = (element: any) => {
  return inputTypes.has(element.type);
};

export const dispatchMouseEventOnTarget = (target: Element, type = 'click') => {
  const event = new MouseEvent(type, { bubbles: true });
  target.dispatchEvent(event);
};
